import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { BasketItem } from "../../../../models/BasketItem";
import {
  BasketSurveyItem,
  Survey2AssortSaver,
  SurveyAnswersList,
  validateRequiredAnswers,
} from "../../../../models/Survey";
import { GET_SURVEY_URL, SAVE_SURVEY_ANSWERS_URL } from "../../../../api/apiRoutes";
import useApiRequest from "../../../../../utils/hooks/useApiRequest";
import useSilentApiRequest from "../../../../../utils/hooks/useSilentApiRequest";
import useFacility from "../../../../../utils/hooks/useFacility";
import type { ApiResult } from "../../../../models/ApiResult";
import { normalizeSurveyMapFromApi } from "../utils/normalizeSurveyMapFromApi";
import { isSurveySaveFailure } from "../utils/isSurveySaveFailure";
import { buildSurveyAnswersEntry } from "../utils/surveyAnswersPayload";
import {
  buildSurveyDraftFingerprint,
  clearSurveyDraft,
  loadSurveyDraft,
  saveSurveyDraft,
} from "../utils/surveyDraftStorage";

type SurveyFetchStatus = "idle" | "loading" | "success" | "error";

export interface UseBasketSurveyOptions {
  surveyStepActive: boolean;
}

const buildSurveyItems = (basket: BasketItem[], surveyMap: Record<string, Survey2AssortSaver>): BasketSurveyItem[] => {
  const items: BasketSurveyItem[] = [];
  basket.forEach((basketItem, lineIndex) => {
    if (!basketItem.hasSurvey) return;
    const survey = surveyMap[basketItem.assortIds];
    if (!survey) return;
    for (let i = 0; i < basketItem.qnt; i++) {
      items.push({
        assortName: basketItem.assortName,
        assortIds: basketItem.assortIds,
        validFrom: basketItem.validFrom ?? null,
        price: basketItem.price,
        basketLineIndex: lineIndex,
        instanceIndex: i,
        survey,
      });
    }
  });
  return items;
};

export const useBasketSurvey = (basket: BasketItem[], options: UseBasketSurveyOptions) => {
  const { surveyStepActive } = options;
  const { get } = useApiRequest();
  const { post: postSilent } = useSilentApiRequest();
  const facilityIds = useFacility();

  const [surveyMap, setSurveyMap] = useState<Record<string, Survey2AssortSaver>>({});
  const [fetchStatus, setFetchStatus] = useState<SurveyFetchStatus>("idle");
  const [isSaving, setIsSaving] = useState(false);
  const [surveySaveError, setSurveySaveError] = useState(false);

  const hasSurvey = basket.some((item) => item.hasSurvey);
  const draftFingerprint = useMemo(() => buildSurveyDraftFingerprint(basket), [basket]);

  useEffect(() => {
    if (!surveyStepActive || !hasSurvey) {
      setFetchStatus("idle");
      setSurveyMap({});
      return;
    }

    let cancelled = false;
    setFetchStatus("loading");
    setSurveyMap({});

    const fetchSurveys = async () => {
      const { result, isError } = await get(GET_SURVEY_URL(facilityIds));
      if (cancelled) return;
      if (!isError && Array.isArray(result)) {
        setSurveyMap(normalizeSurveyMapFromApi(result));
        setFetchStatus("success");
      } else {
        setSurveyMap({});
        setFetchStatus("error");
      }
    };

    fetchSurveys();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyStepActive, hasSurvey, facilityIds]);

  const surveyItems = useMemo(() => buildSurveyItems(basket, surveyMap), [basket, surveyMap]);

  const isLoadingSurvey = surveyStepActive && hasSurvey && (fetchStatus === "idle" || fetchStatus === "loading");
  const surveyLoadError = fetchStatus === "error" || (fetchStatus === "success" && surveyItems.length === 0);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [allAnswers, setAllAnswers] = useState<Record<string, string>[]>(() => surveyItems.map(() => ({})));
  const [invalidQuestionIds, setInvalidQuestionIds] = useState<Set<string>>(new Set());
  const [firstInvalidQuestionId, setFirstInvalidQuestionId] = useState<string | null>(null);
  const [surveyValidationFailSeq, setSurveyValidationFailSeq] = useState(0);
  const isHydratingRef = useRef(false);

  const surveyItemsLength = surveyItems.length;

  useEffect(() => {
    // Nie czyść draftu gdy koszyk jest jeszcze pusty (pierwszy render po powrocie z innej strony).
    // Wtedy getBasket() jeszcze nie zdążył załadować danych i hasSurvey === false.
    if (basket.length > 0 && (!hasSurvey || !draftFingerprint)) {
      clearSurveyDraft(facilityIds);
    }
  }, [hasSurvey, draftFingerprint, facilityIds, basket.length]);

  useEffect(() => {
    if (surveyItemsLength === 0) return;

    isHydratingRef.current = true;
    const draft = loadSurveyDraft(facilityIds, draftFingerprint);
    if (draft && draft.allAnswers.length === surveyItemsLength) {
      setCurrentIndex(Math.min(draft.currentIndex, surveyItemsLength - 1));
      setAllAnswers(draft.allAnswers);
      return;
    }

    setCurrentIndex(0);
    setAllAnswers(Array.from({ length: surveyItemsLength }, () => ({})));
    if (draft) {
      clearSurveyDraft(facilityIds);
    }
  }, [surveyItemsLength, draftFingerprint, facilityIds]);

  useEffect(() => {
    if (isHydratingRef.current) {
      isHydratingRef.current = false;
      return;
    }
    if (!surveyStepActive || surveyItemsLength === 0 || !draftFingerprint) return;

    saveSurveyDraft(facilityIds, {
      fingerprint: draftFingerprint,
      currentIndex,
      allAnswers,
    });
  }, [allAnswers, currentIndex, surveyStepActive, surveyItemsLength, draftFingerprint, facilityIds]);

  const setAnswer = (itemIndex: number, qId: string, value: string) => {
    setSurveySaveError(false);
    setAllAnswers((prev) => {
      const updated = [...prev];
      updated[itemIndex] = { ...updated[itemIndex], [qId]: value };
      return updated;
    });
    setInvalidQuestionIds((prev) => {
      if (!prev.has(qId)) return prev;
      const next = new Set(prev);
      next.delete(qId);
      return next;
    });
  };

  const validate = (itemIndex: number): boolean => {
    const item = surveyItems[itemIndex];
    if (!item) return true;
    const answers = allAnswers[itemIndex] || {};
    const { valid, invalidIds, firstInvalidId } = validateRequiredAnswers(item.survey, answers);

    setInvalidQuestionIds(invalidIds);
    if (valid) {
      setFirstInvalidQuestionId(null);
      return true;
    }
    setFirstInvalidQuestionId(firstInvalidId);
    setSurveyValidationFailSeq((s) => s + 1);
    return false;
  };

  const goNext = (onFinish: () => void): boolean => {
    if (!validate(currentIndex)) return false;
    if (currentIndex < surveyItems.length - 1) {
      setCurrentIndex((prev) => prev + 1);
      setInvalidQuestionIds(new Set());
    } else {
      onFinish();
    }
    return true;
  };

  const goBack = (onBasket: () => void) => {
    setSurveySaveError(false);
    setFirstInvalidQuestionId(null);
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
      setInvalidQuestionIds(new Set());
    } else {
      onBasket();
    }
  };

  const saveSurveyAnswers = useCallback(async (): Promise<boolean> => {
    setSurveySaveError(false);
    setIsSaving(true);

    try {
      const surveyList = surveyItems.map((surveyItem, itemIdx) => {
        const line = basket[surveyItem.basketLineIndex];
        const itemAnswers = allAnswers[itemIdx] || {};
        return buildSurveyAnswersEntry(surveyItem, line, itemAnswers);
      });

      const body: SurveyAnswersList = {
        surveyList,
        facilityIds,
      };

      const response = (await postSilent(SAVE_SURVEY_ANSWERS_URL, body)) as ApiResult | undefined;

      if (isSurveySaveFailure(response)) {
        setSurveySaveError(true);
        return false;
      }

      clearSurveyDraft(facilityIds);
      return true;
    } catch {
      setSurveySaveError(true);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [basket, surveyItems, allAnswers, facilityIds, postSilent]);

  const currentItem = surveyItems[currentIndex] ?? null;
  const currentAnswers = allAnswers[currentIndex] ?? {};

  return {
    hasSurvey,
    isLoadingSurvey,
    surveyLoadError,
    isSaving,
    surveySaveError,
    surveyItems,
    currentIndex,
    currentItem,
    currentAnswers,
    setAnswer,
    goNext,
    goBack,
    invalidQuestionIds,
    firstInvalidQuestionId,
    surveyValidationFailSeq,
    saveSurveyAnswers,
  };
};
